/**
 * Overview v17 — bento düzeninde, tek bakışta "işler nasıl, neye bakmalıyım".
 * v16 çerçevesinde çizilir (App.js V16_SHELL_PATHS). Canlı Overview2'den bağımsız.
 *
 * Veri (hepsi sunucuda toplanmış, küçük cevaplar):
 *  · /api/custom-reports/query → GA4 günlük, reklam kanalları, kaynak/ortam, cihaz, kampanya,
 *    haftanın günü, Search Console. Dönemler dünle biter (bugünün DB verisi yarım) ve
 *    önceki eşit dönemle kıyaslanır. "Today" seçiliyken analiz kutuları son 7 günü gösterir.
 *  · /api/brands/:id/live/<kanal>/today → "Right now" ve Today KPI'ları (kıyas yok:
 *    yarım gün ≠ tam gün).
 *  · /api/pacing/overview → kanal başına ay temposu (hedef girildiyse).
 *  · /api/brand-info/:id/details → logo, site.
 *
 * 🔑 Meta canlı "today" verisinde dönüşüm yok (conversionsAvailable:false) — kopma değil.
 * 🔑 Criteo kaynağında conversion_value yok → Criteo için ROAS/ciro yorumlanmaz.
 * 🔑 Search Console gecikmeli/eksik gelebiliyor → kutu son mevcut güne göre hesaplanır,
 *    5 günden eskiyse Signals'ta uyarı çıkar.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell,
} from 'recharts';
import {
  ArrowUpRight, ArrowDownRight, ChevronRight, AlertTriangle, Search, Smartphone, Monitor, Tablet,
} from 'lucide-react';

const API = 'https://api.glowytics.com';
const BLUE = '#0071e3';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: 'mtd', label: 'MTD' },
];

const CHANNELS = {
  'Google Ads': { logo: '/google_ads-512.webp', href: '/ads-platforms/google-ads', live: 'google-ads', pacing: 'GOOGLE_ADS', color: '#4285f4', reportsRevenue: true },
  'Meta Ads': { logo: '/6033716.png', href: '/ads-platforms/meta-overview', live: 'meta-ads', pacing: 'META_ADS', color: '#0866ff', reportsRevenue: true },
  'TikTok Ads': { logo: '/tik-tokiconss.png', href: '/ads-platforms/tiktok-ads/campaign-type', live: 'tiktok-ads', pacing: 'TIKTOK_ADS', color: '#111111', reportsRevenue: true },
  Pinterest: { logo: '/pinterestlogopng.png', href: '/pinterest-dashboard', live: 'pinterest-ads', pacing: 'PINTEREST_ADS', color: '#e60023', reportsRevenue: true },
  Criteo: { logo: '/CRcriteopng.png', href: '/ads-platforms/criteo-overview', live: null, pacing: 'CRITEO_ADS', color: '#fe5000', reportsRevenue: false },
  'RTB House': { logo: '/RTBhouseiconPNG.png', href: '/market-insights/rtbhouse-connect', live: 'rtb-house', pacing: 'RTB_HOUSE', color: '#d6202b', reportsRevenue: true },
};
const SOURCE_TO_CHANNEL = {
  google_ads: 'Google Ads', meta_ads: 'Meta Ads', tiktok_ads: 'TikTok Ads',
  pinterest: 'Pinterest', criteo: 'Criteo', rtb_house: 'RTB House',
};
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_LONG = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };

const TREND_METRICS = [
  { key: 'revenue', label: 'Revenue', kind: 'money' },
  { key: 'spend', label: 'Spend', kind: 'money' },
  { key: 'roas', label: 'ROAS', kind: 'x' },
  { key: 'sessions', label: 'Sessions', kind: 'int' },
  { key: 'orders', label: 'Orders', kind: 'int' },
];

/* ── tarih & biçim ────────────────────────────────────────────────────── */

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const parse = (s) => new Date(`${s}T00:00:00`);
const shortDate = (s) => parse(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const daysBetween = (a, b) => Math.round((parse(b) - parse(a)) / 864e5) + 1;

function makeFmt(currency) {
  const sym = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' }[currency] || `${currency} `;
  const ok = (n) => n != null && Number.isFinite(n);
  return {
    money: (n) => (ok(n) ? `${sym}${Math.round(n).toLocaleString('en-US')}` : '—'),
    compact: (n) => {
      if (!ok(n)) return '—';
      const a = Math.abs(n);
      if (a >= 1e6) return `${sym}${(n / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M`;
      if (a >= 1e3) return `${sym}${(n / 1e3).toFixed(a >= 1e5 ? 0 : 1)}K`;
      return `${sym}${Math.round(n)}`;
    },
    int: (n) => (ok(n) ? Math.round(n).toLocaleString('en-US') : '—'),
    kint: (n) => (!ok(n) ? '—' : n >= 1e4 ? `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1)}K` : Math.round(n).toLocaleString('en-US')),
    x: (n) => (ok(n) ? `${n.toFixed(2)}x` : '—'),
    pct: (n, d = 2) => (ok(n) ? `${(n * 100).toFixed(d)}%` : '—'),
  };
}

const div = (a, b) => (b ? a / b : null);
const delta = (cur, prev) => (cur != null && prev ? (cur - prev) / prev : null);
const pctText = (d) => `${Math.abs(d * 100).toFixed(Math.abs(d) < 0.1 ? 1 : 0)}%`;

/** Dönem aralığı + önceki eşit dönem. Dünle biter. */
function rangeFor(period, now = new Date()) {
  const y = addDays(now, -1);
  if (period === 'mtd') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = now.getDate() === 1 ? start : y;
    const len = daysBetween(ymd(start), ymd(end));
    const pStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { start: ymd(start), end: ymd(end), prevStart: ymd(pStart), prevEnd: ymd(addDays(pStart, len - 1)), vs: 'same days last month' };
  }
  const days = period === '30d' ? 30 : 7;
  const start = addDays(y, -(days - 1));
  return { start: ymd(start), end: ymd(y), prevStart: ymd(addDays(start, -days)), prevEnd: ymd(addDays(start, -1)), vs: `previous ${days} days` };
}

/* ── veri ─────────────────────────────────────────────────────────────── */

async function getJson(url, brandId, body) {
  const res = await fetch(url, {
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    headers: { 'X-Brand-ID': brandId, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}
const q = (brandId, body) => getJson(`${API}/api/custom-reports/query`, brandId, body).catch(() => null);

const CORE_PARTS = [
  { id: 'g', source: 'ga4_daily', metrics: ['sessions', 'transactions', 'revenue'] },
  { id: 'a', source: 'ads_all', metrics: ['cost', 'clicks', 'impressions'] },
];
const pickCore = (t) => (t ? {
  revenue: t['g:revenue'] ?? 0, sessions: t['g:sessions'] ?? 0, orders: t['g:transactions'] ?? 0,
  spend: t['a:cost'] ?? 0, clicks: t['a:clicks'] ?? 0, impressions: t['a:impressions'] ?? 0,
} : null);
const derive = (c) => c && ({
  ...c,
  roas: div(c.revenue, c.spend),
  cr: div(c.orders, c.sessions),
  aov: div(c.revenue, c.orders),
  cpo: div(c.spend, c.orders),
  ctr: div(c.clicks, c.impressions),
});

/* ── küçük parçalar ───────────────────────────────────────────────────── */

const Tile = ({ className = '', children }) => (
  <section className={`rounded-[26px] bg-[#f5f5f7] ${className}`}>{children}</section>
);

function TileHead({ title, sub, right, icon }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="flex items-center gap-1.5 text-[15px] font-semibold tracking-[-0.01em] text-gray-900">{icon}{title}</h3>
        {sub && <p className="mt-0.5 text-xs text-gray-400 truncate">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

function Delta({ value, invert = false, className = '' }) {
  if (value == null || !Number.isFinite(value)) return null;
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${good ? 'text-emerald-600' : 'text-rose-600'} ${className}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />{pctText(value)}
    </span>
  );
}

function Segmented({ options, value, onChange, size = 'md' }) {
  return (
    <div className="inline-flex rounded-full bg-[#ebebef] p-1">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={`rounded-full font-medium transition-all duration-200
            ${size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-1.5 text-[13px]'}
            ${value === o.key ? 'bg-white text-gray-900 shadow-[0_1px_3px_rgba(0,0,0,0.08)]' : 'text-gray-500 hover:text-gray-800'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const Skeleton = ({ className = '' }) => <div className={`animate-pulse rounded-lg bg-black/[0.05] ${className}`} />;

function Spark({ data, color = BLUE, height = 34 }) {
  if (!data || data.length < 2) return <div style={{ height }} />;
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.75} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Marka logosu: kayıtlı logo → sitenin favicon'u → baş harf (dış logo çoğu zaman yüklenmiyor). */
function BrandMark({ logo, site, name }) {
  const candidates = [logo, site && `https://www.google.com/s2/favicons?domain=${site}&sz=128`].filter(Boolean);
  const [i, setI] = useState(0);
  useEffect(() => { setI(0); }, [logo, site]);
  const src = candidates[i];
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] ring-1 ring-black/[0.04]">
      {src
        ? <img src={src} alt={name} className="max-h-9 max-w-[44px] object-contain" onError={() => setI((n) => n + 1)} />
        : <span className="text-xl font-semibold text-gray-700">{(name || 'G').charAt(0)}</span>}
    </div>
  );
}

const Mini = ({ label, value }) => (
  <div className="rounded-2xl bg-white px-3 py-2.5 min-w-0">
    <div className="text-[11px] text-gray-400 truncate">{label}</div>
    <div className="text-[17px] font-semibold tabular-nums tracking-[-0.01em]">{value}</div>
  </div>
);

/* ── sayfa ────────────────────────────────────────────────────────────── */

export default function OverviewV17() {
  const { user, selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId');

  const [period, setPeriod] = useState('7d');
  const [brand, setBrand] = useState(null);
  const [connected, setConnected] = useState(null);
  const [currency, setCurrency] = useState('TRY');
  const [pacing, setPacing] = useState(null);
  const [live, setLive] = useState(null);
  const [month, setMonth] = useState(null);
  const [weekday, setWeekday] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [data, setData] = useState(null);
  const [trendMetric, setTrendMetric] = useState('revenue');
  const [trendRange, setTrendRange] = useState('30');
  const [trend, setTrend] = useState(null);

  const fmt = useMemo(() => makeFmt(currency), [currency]);

  /* Dönemden bağımsız: marka, bağlantılar, tempo, canlı, ay görünümü, haftanın günü, GSC */
  useEffect(() => {
    if (!brandId) return undefined;
    let alive = true;
    const set = (fn) => (v) => { if (alive) fn(v); };
    setBrand(null); setConnected(null); setPacing(null); setLive(null); setMonth(null); setWeekday(null); setGsc(null);

    getJson(`${API}/api/brand-info/${brandId}/details`, brandId).then(set(setBrand)).catch(() => {});
    getJson(`${API}/api/pacing/overview`, brandId).then(set(setPacing)).catch(() => set(setPacing)({ channels: [] }));
    getJson(`${API}/api/custom-reports/sources`, brandId).then((d) => {
      if (!alive) return;
      setCurrency(d.currency || 'TRY');
      const list = d.sources.filter((s) => s.connected && SOURCE_TO_CHANNEL[s.key]).map((s) => SOURCE_TO_CHANNEL[s.key]);
      setConnected(list);
      const hasGsc = d.sources.some((s) => s.key === 'search_console' && s.connected);

      // Right now — canlı
      const liveOf = (p) => getJson(`${API}/api/brands/${brandId}/live/${p}/today`, brandId).catch(() => null);
      const withLive = list.filter((c) => CHANNELS[c].live);
      Promise.all([liveOf('ga4'), ...withLive.map((c) => liveOf(CHANNELS[c].live))]).then(([ga, ...rows]) => {
        const channels = withLive.map((name, i) => ({ name, r: rows[i] })).filter((x) => x.r && x.r.success !== false);
        set(setLive)({
          ga,
          at: new Date(),
          channels: channels.map(({ name, r }) => ({
            name,
            spend: Number(r.cost || 0),
            revenue: r.conversionsAvailable === false || r.revenue == null ? null : Number(r.revenue),
            clicks: r.clicks ?? (r.campaigns || []).reduce((s, c) => s + Number(c.clicks || 0), 0),
            impressions: r.impressions ?? (r.campaigns || []).reduce((s, c) => s + Number(c.impressions || 0), 0),
          })),
        });
      });

      // GSC: son mevcut güne göre 7 gün (veri gecikmeli/eksik gelebiliyor)
      if (hasGsc) {
        const end = addDays(new Date(), -1);
        q(brandId, { start: ymd(addDays(end, -45)), end: ymd(end), dimensions: ['date_day'], parts: [{ id: 's', source: 'search_console', metrics: ['clicks'] }] })
          .then(async (r) => {
            const last = r?.rows?.length ? r.rows[r.rows.length - 1].dims[0] : null;
            if (!last) { set(setGsc)({ last: null }); return; }
            const s = addDays(parse(last), -6);
            const [tot, top] = await Promise.all([
              q(brandId, { start: ymd(s), end: last, compare: 'previous', parts: [{ id: 's', source: 'search_console', metrics: ['clicks', 'impressions', 'ctr', 'avg_position'] }] }),
              q(brandId, { start: ymd(s), end: last, dimensions: ['query'], parts: [{ id: 's', source: 'search_console', metrics: ['clicks', 'avg_position'] }], sort: { key: 's:clicks', dir: 'desc' }, limit: 5 }),
            ]);
            set(setGsc)({ last, start: ymd(s), cur: tot?.totals, prev: tot?.compareTotals, top: top?.rows || [] });
          });
      } else {
        set(setGsc)({ none: true });
      }
    }).catch(() => set(setConnected)([]));

    // Ay görünümü: bu ay (dünle biter) / geçen ayın aynı günleri / geçen ayın tamamı
    const now = new Date();
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const pmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const pmEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const elapsed = now.getDate() - 1;
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    if (elapsed >= 1) {
      Promise.all([
        q(brandId, { start: ymd(mStart), end: ymd(addDays(now, -1)), parts: CORE_PARTS }),
        q(brandId, { start: ymd(pmStart), end: ymd(addDays(pmStart, elapsed - 1)), parts: CORE_PARTS }),
        q(brandId, { start: ymd(pmStart), end: ymd(pmEnd), parts: CORE_PARTS }),
      ]).then(([m, pm, full]) => set(setMonth)({
        elapsed,
        daysInMonth,
        cur: pickCore(m?.totals),
        same: pickCore(pm?.totals),
        lastFull: pickCore(full?.totals),
        name: now.toLocaleDateString('en-US', { month: 'long' }),
        lastName: pmStart.toLocaleDateString('en-US', { month: 'long' }),
      }));
    } else {
      setMonth({ elapsed: 0 });
    }

    // Haftanın günü: son 8 hafta (ISO: 1 = Pazartesi)
    const wEnd = addDays(now, -1);
    q(brandId, { start: ymd(addDays(wEnd, -55)), end: ymd(wEnd), dimensions: ['weekday'], parts: [{ id: 'g', source: 'ga4_daily', metrics: ['revenue', 'transactions', 'sessions'] }] })
      .then((r) => set(setWeekday)((r?.rows || []).map((x) => ({
        day: WEEKDAYS[Number(x.dims[0]) - 1],
        revenue: (x['g:revenue'] || 0) / 8,
        cr: div(x['g:transactions'], x['g:sessions']),
      }))));

    return () => { alive = false; };
  }, [brandId]);

  /* Dönem verisi */
  useEffect(() => {
    if (!brandId || !connected) return undefined;
    let alive = true;
    setData(null);
    const ar = rangeFor(period === 'today' ? '7d' : period); // analiz aralığı
    const chParts = [{ id: 'a', source: 'ads_all', metrics: ['cost', 'conversion_value', 'clicks'] }];
    Promise.all([
      q(brandId, { start: ar.start, end: ar.end, dimensions: ['date_day'], parts: CORE_PARTS }),
      q(brandId, { start: ar.prevStart, end: ar.prevEnd, dimensions: ['date_day'], parts: CORE_PARTS }),
      q(brandId, { start: ar.start, end: ar.end, dimensions: ['platform'], parts: chParts }),
      q(brandId, { start: ar.prevStart, end: ar.prevEnd, dimensions: ['platform'], parts: chParts }),
      q(brandId, { start: ar.start, end: ar.end, dimensions: ['source_medium'], parts: [{ id: 't', source: 'ga4_traffic', metrics: ['sessions', 'revenue'] }], sort: { key: 't:revenue', dir: 'desc' }, limit: 7 }),
      q(brandId, { start: ar.prevStart, end: ar.prevEnd, dimensions: ['source_medium'], parts: [{ id: 't', source: 'ga4_traffic', metrics: ['revenue'] }], sort: { key: 't:revenue', dir: 'desc' }, limit: 40 }),
      q(brandId, { start: ar.start, end: ar.end, dimensions: ['device'], parts: [{ id: 'd', source: 'ga4_device', metrics: ['sessions', 'transactions', 'revenue'] }] }),
      q(brandId, { start: ar.start, end: ar.end, dimensions: ['platform', 'campaign'], parts: [{ id: 'a', source: 'ads_all', metrics: ['cost', 'conversion_value'] }], sort: { key: 'a:cost', dir: 'desc' }, limit: 40 }),
    ]).then(([cur, prev, ch, chPrev, src, srcPrev, dev, camp]) => {
      if (!alive) return;
      const prevRows = prev?.rows || [];
      const series = (cur?.rows || []).map((r, i) => ({
        date: r.dims[0],
        c: derive(pickCore(r)),
        p: prevRows[i] ? derive(pickCore(prevRows[i])) : null,
      }));
      const prevCh = Object.fromEntries((chPrev?.rows || []).map((x) => [x.dims[0], x['a:cost']]));
      const prevSrc = Object.fromEntries((srcPrev?.rows || []).map((x) => [x.dims[0], x['t:revenue']]));
      setData({
        period,
        range: ar,
        cur: derive(pickCore(cur?.totals)),
        prev: derive(pickCore(prev?.totals)),
        series,
        channels: (ch?.rows || [])
          .filter((x) => CHANNELS[x.dims[0]] && connected.includes(x.dims[0]) && ((x['a:cost'] || 0) > 0 || (prevCh[x.dims[0]] || 0) > 0))
          .map((x) => ({ name: x.dims[0], spend: x['a:cost'] || 0, revenue: x['a:conversion_value'], prevSpend: prevCh[x.dims[0]] ?? null }))
          .sort((a, b) => b.spend - a.spend),
        sources: (src?.rows || []).map((x) => ({ name: x.dims[0], sessions: x['t:sessions'], revenue: x['t:revenue'], prev: prevSrc[x.dims[0]] ?? null })),
        devices: { rows: (dev?.rows || []).map((x) => ({ name: x.dims[0], sessions: x['d:sessions'], orders: x['d:transactions'], revenue: x['d:revenue'] })), sessions: dev?.totals?.['d:sessions'] || 0 },
        campaigns: (camp?.rows || []).map((x) => ({ platform: x.dims[0], name: x.dims[1], spend: x['a:cost'] || 0, revenue: x['a:conversion_value'] })),
      });
    });
    return () => { alive = false; };
  }, [brandId, connected, period]);

  /* Trend */
  useEffect(() => {
    if (!brandId) return undefined;
    let alive = true;
    setTrend(null);
    const days = Number(trendRange);
    const end = addDays(new Date(), -1);
    const start = addDays(end, -(days - 1));
    const pStart = addDays(start, -days);
    Promise.all([
      q(brandId, { start: ymd(start), end: ymd(end), dimensions: ['date_day'], parts: CORE_PARTS }),
      q(brandId, { start: ymd(pStart), end: ymd(addDays(start, -1)), dimensions: ['date_day'], parts: CORE_PARTS }),
    ]).then(([cur, prev]) => {
      if (!alive) return;
      const byDate = (rows) => Object.fromEntries((rows || []).map((x) => [x.dims[0], derive(pickCore(x))]));
      const c = byDate(cur?.rows);
      const p = byDate(prev?.rows);
      const out = [];
      for (let i = 0; i < days; i++) out.push({ date: ymd(addDays(start, i)), cur: c[ymd(addDays(start, i))] || null, prev: p[ymd(addDays(pStart, i))] || null });
      setTrend(out);
    });
    return () => { alive = false; };
  }, [brandId, trendRange]);

  /* Görünen değerler: Today → canlı, diğerleri → dönem */
  const isToday = period === 'today';
  const liveTot = useMemo(() => {
    if (!live) return null;
    const sum = (k) => live.channels.reduce((s, c) => s + (c[k] || 0), 0);
    return derive({
      revenue: Number(live.ga?.revenue || 0), sessions: Number(live.ga?.sessions || 0), orders: Number(live.ga?.purchases || 0),
      spend: sum('spend'), clicks: sum('clicks'), impressions: sum('impressions'),
    });
  }, [live]);
  const fresh = data?.period === period;
  const view = isToday ? liveTot : fresh ? data.cur : null;
  const prev = isToday ? null : fresh ? data.prev : null;
  const series = !isToday && fresh ? data.series : null;
  const sp = (k) => series?.map((s) => ({ v: s.c?.[k] ?? null }));

  const signals = useMemo(() => buildSignals({ data, gsc, pacing, month, fmt }), [data, gsc, pacing, month, fmt]);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 5 ? 'Good night' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (user?.name || user?.firstName || '').split(' ')[0];
  const ar = data?.range;
  const anaLabel = ar ? `${shortDate(ar.start)} – ${shortDate(ar.end)}${isToday ? ' · last 7 days' : ''}` : '';
  const site = brand?.websiteUrl?.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

  return (
    <div className="text-gray-900 antialiased">
      {/* ── Başlık ───────────────────────────────────────────── */}
      <header className="flex flex-wrap items-center justify-between gap-x-8 gap-y-5">
        <div className="flex min-w-0 items-center gap-4">
          <BrandMark logo={brand?.logoUrl} site={site} name={selectedBrand?.name} />
          <div className="min-w-0">
            <div className="truncate text-[12px] font-semibold uppercase tracking-[0.08em] text-gray-400">
              {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · {selectedBrand?.name}
              {site && <a href={brand.websiteUrl} target="_blank" rel="noreferrer" className="ml-1.5 normal-case tracking-normal font-medium hover:text-gray-600">{site}</a>}
            </div>
            <h1 className="mt-0.5 text-[30px] leading-tight font-semibold tracking-[-0.02em]">{greeting}{firstName ? `, ${firstName}` : ''}</h1>
          </div>
        </div>
        <Segmented options={PERIODS} value={period} onChange={setPeriod} />
      </header>
      <p className="mt-3 max-w-[920px] text-[17px] leading-relaxed text-gray-500">
        <Headline isToday={isToday} cur={view} prev={prev} fmt={fmt} vs={ar?.vs} signals={signals} />
      </p>

      {/* ── Bento ────────────────────────────────────────────── */}
      <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-6 xl:grid-cols-12">
        {/* Ciro */}
        <Tile className="flex flex-col p-6 md:col-span-6 xl:col-span-5">
          <div className="text-[13px] font-medium text-gray-500">{isToday ? 'Revenue so far today' : 'Revenue'}</div>
          {view ? (
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <span className="text-[52px] leading-none font-semibold tracking-[-0.035em] tabular-nums">{fmt.money(view.revenue)}</span>
              <Delta value={delta(view.revenue, prev?.revenue)} className="!text-sm" />
            </div>
          ) : <Skeleton className="mt-2 h-12 w-64" />}
          <div className="mt-1.5 text-xs text-gray-400">
            {isToday
              ? (live ? `GA4 · live · updated ${live.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : '')
              : ar && `GA4 · ${shortDate(ar.start)} – ${shortDate(ar.end)} · vs ${ar.vs}`}
          </div>
          <div className="mt-4 min-h-[170px] flex-1">
            {series?.length > 1 ? <HeroChart series={series} fmt={fmt} /> : isToday ? <LiveSplit live={live} fmt={fmt} /> : <Skeleton className="h-full w-full" />}
          </div>
        </Tile>

        {/* KPI kutuları */}
        <div className="grid grid-cols-2 gap-4 md:col-span-6 lg:grid-cols-4 xl:col-span-7">
          <Kpi label="Ad spend" value={view && fmt.money(view.spend)} d={delta(view?.spend, prev?.spend)} invert spark={sp('spend')} />
          <Kpi label="Blended ROAS" value={view && fmt.x(view.roas)} d={delta(view?.roas, prev?.roas)} spark={sp('roas')} hint="Revenue ÷ ad spend" />
          <Kpi label="Orders" value={view && fmt.int(view.orders)} d={delta(view?.orders, prev?.orders)} spark={sp('orders')} />
          <Kpi label="Conversion rate" value={view && fmt.pct(view.cr)} d={delta(view?.cr, prev?.cr)} spark={sp('cr')} />
          <Kpi label="Sessions" value={view && fmt.int(view.sessions)} d={delta(view?.sessions, prev?.sessions)} spark={sp('sessions')} />
          <Kpi label="Avg. order value" value={view && fmt.money(view.aov)} d={delta(view?.aov, prev?.aov)} spark={sp('aov')} />
          <Kpi label="Cost per order" value={view && fmt.money(view.cpo)} d={delta(view?.cpo, prev?.cpo)} invert spark={sp('cpo')} />
          <Kpi label="Ad CTR" value={view && fmt.pct(view.ctr)} d={delta(view?.ctr, prev?.ctr)} spark={sp('ctr')} hint={view && `${fmt.kint(view.clicks)} clicks`} />
        </div>

        <RightNow live={live} liveTot={liveTot} fmt={fmt} className="md:col-span-3 xl:col-span-4" />
        <MonthOutlook month={month} fmt={fmt} className="md:col-span-3 xl:col-span-4" />
        <Signals signals={signals} loading={!data} className="md:col-span-6 xl:col-span-4" />

        <Channels data={data} connected={connected} pacing={pacing} fmt={fmt} sub={anaLabel} className="md:col-span-6 xl:col-span-7" />
        <Sources data={data} fmt={fmt} sub={anaLabel} className="md:col-span-6 xl:col-span-5" />

        <Campaigns data={data} fmt={fmt} sub={anaLabel} className="md:col-span-6 xl:col-span-7" />
        <Devices data={data} fmt={fmt} sub={anaLabel} className="md:col-span-6 xl:col-span-5" />

        <Tile className="p-6 md:col-span-6 xl:col-span-8">
          <TileHead
            title="Trend"
            sub={`Last ${trendRange} days vs previous ${trendRange}`}
            right={(
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Segmented size="sm" options={TREND_METRICS} value={trendMetric} onChange={setTrendMetric} />
                <Segmented size="sm" options={[{ key: '30', label: '30D' }, { key: '90', label: '90D' }]} value={trendRange} onChange={setTrendRange} />
              </div>
            )}
          />
          <div className="mt-5 h-[260px]">
            {trend ? <TrendChart data={trend} metric={TREND_METRICS.find((m) => m.key === trendMetric)} fmt={fmt} /> : <Skeleton className="h-full w-full" />}
          </div>
        </Tile>
        <Weekdays rows={weekday} fmt={fmt} className="md:col-span-6 xl:col-span-4" />

        <SearchTile gsc={gsc} fmt={fmt} className="md:col-span-6 xl:col-span-12" />
      </div>
      <div className="h-4" />
    </div>
  );
}

/* ── başlık cümlesi & sinyaller ───────────────────────────────────────── */

function Headline({ isToday, cur, prev, fmt, vs, signals }) {
  if (!cur) return <span className="inline-block h-5 w-96 animate-pulse rounded bg-black/[0.05] align-middle" />;
  const warn = signals.filter((s) => s.tone === 'bad').length;
  const tail = warn ? <> {warn === 1 ? 'One thing needs' : `${warn} things need`} your attention.</> : null;
  if (isToday) {
    return <>{fmt.money(cur.revenue)} in revenue so far today from {fmt.int(cur.sessions)} sessions, on {fmt.money(cur.spend)} of ad spend.{tail}</>;
  }
  const dr = delta(cur.revenue, prev?.revenue);
  const ds = delta(cur.spend, prev?.spend);
  if (dr == null) return <>{fmt.money(cur.revenue)} in revenue.{tail}</>;
  return (
    <>
      Revenue is <span className={dr >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{dr >= 0 ? 'up' : 'down'} {pctText(dr)}</span> on the {vs}
      {ds != null && <>, with ad spend {ds >= 0 ? 'up' : 'down'} {pctText(ds)}</>}
      {cur.roas != null && <> and blended ROAS at {fmt.x(cur.roas)}</>}.{tail}
    </>
  );
}

/** Kurala dayalı sinyaller — yorum değil, ölçülebilir eşikler. En fazla 5. */
function buildSignals({ data, gsc, pacing, month, fmt }) {
  const out = [];
  if (data) {
    const total = data.campaigns.reduce((s, c) => s + c.spend, 0);
    // Harcamanın ≥%3'ünü alıp ROAS'ı 1'in altında kalan kampanyalar (ciro raporlayan kanallarda)
    data.campaigns
      .filter((c) => CHANNELS[c.platform]?.reportsRevenue && total && c.spend / total >= 0.03 && c.revenue != null && c.revenue / c.spend < 1)
      .slice(0, 2)
      .forEach((c) => out.push({ tone: 'bad', title: `${c.name} returns ${fmt.x(c.revenue / c.spend)}`, text: `${fmt.compact(c.spend)} spent on ${c.platform}, ${fmt.compact(c.revenue)} attributed.`, href: CHANNELS[c.platform]?.href }));
    // Kanal harcaması ±%25'ten fazla değiştiyse
    data.channels.filter((c) => c.prevSpend > 1000 && Math.abs(delta(c.spend, c.prevSpend)) > 0.25).forEach((c) => {
      const d = delta(c.spend, c.prevSpend);
      out.push({ tone: 'info', title: `${c.name} spend ${d > 0 ? 'up' : 'down'} ${pctText(d)}`, text: `${fmt.compact(c.prevSpend)} → ${fmt.compact(c.spend)} on the ${data.range.vs}.`, href: CHANNELS[c.name]?.href });
    });
    // Dönüşüm oranı belirgin düştüyse
    const dcr = delta(data.cur?.cr, data.prev?.cr);
    if (dcr != null && dcr < -0.15) out.push({ tone: 'bad', title: `Conversion rate down ${pctText(dcr)}`, text: `${fmt.pct(data.prev.cr)} → ${fmt.pct(data.cur.cr)} on the ${data.range.vs}.` });
    // En verimli kanal (olumlu)
    const best = data.channels
      .filter((c) => CHANNELS[c.name]?.reportsRevenue && c.spend > 0 && c.revenue)
      .sort((a, b) => b.revenue / b.spend - a.revenue / a.spend)[0];
    if (best && data.channels.length > 1) out.push({ tone: 'good', title: `${best.name} leads at ${fmt.x(best.revenue / best.spend)}`, text: `${fmt.compact(best.revenue)} attributed on ${fmt.compact(best.spend)} of spend.`, href: CHANNELS[best.name]?.href });
  }
  // Tempo: aylık hedefin %10'dan fazla önünde giden kanal
  if (pacing?.days_in_month) {
    const expected = pacing.days_passed / pacing.days_in_month;
    (pacing.channels || []).filter((c) => c.target_spend && Number(c.mtd_spend) / Number(c.target_spend) > expected + 0.1).forEach((c) => {
      out.push({ tone: 'bad', title: `${c.label} is ahead of budget pace`, text: `${Math.round((Number(c.mtd_spend) / Number(c.target_spend)) * 100)}% of the monthly target spent, ${Math.round(expected * 100)}% of the month gone.`, href: '/budget-overview' });
    });
  }
  // Ay sonu tahmini geçen ayın belirgin gerisinde mi
  if (month?.cur && month?.lastFull?.revenue) {
    const proj = (month.cur.revenue / month.elapsed) * month.daysInMonth;
    const d = delta(proj, month.lastFull.revenue);
    if (d != null && d < -0.1) out.push({ tone: 'info', title: `${month.name} is tracking ${pctText(d)} below ${month.lastName}`, text: `Run-rate projection ${fmt.compact(proj)} vs ${fmt.compact(month.lastFull.revenue)}.` });
  }
  // Search Console verisi bayat mı
  if (gsc?.last) {
    const age = Math.round((new Date() - parse(gsc.last)) / 864e5);
    if (age > 5) out.push({ tone: 'bad', title: 'Search Console data is stale', text: `Latest data is from ${shortDate(gsc.last)} (${age} days ago). The sync may be stuck.`, href: '/search-console/overview' });
  }
  const rank = { bad: 0, info: 1, good: 2 };
  return out.sort((a, b) => rank[a.tone] - rank[b.tone]).slice(0, 5);
}

/* ── kutular ──────────────────────────────────────────────────────────── */

function Kpi({ label, value, d, invert, spark, hint }) {
  const good = d == null ? null : (d >= 0) !== !!invert;
  return (
    <Tile className="flex min-h-[132px] flex-col justify-between p-4">
      <div className="text-[13px] font-medium text-gray-500">{label}</div>
      <div>
        {value == null ? <Skeleton className="mt-2 h-7 w-24" /> : (
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
            <span className="text-[24px] leading-tight font-semibold tracking-[-0.02em] tabular-nums">{value}</span>
            <Delta value={d} invert={invert} />
          </div>
        )}
        {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
      </div>
      <div className="mt-2"><Spark data={spark} color={good == null ? '#8e8e93' : good ? '#34c759' : '#ff3b30'} /></div>
    </Tile>
  );
}

const tooltipStyle = { borderRadius: 14, border: 'none', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', fontSize: 12 };

function HeroChart({ series, fmt }) {
  const rows = series.map((s) => ({ date: s.date, cur: s.c?.revenue ?? null, prev: s.p?.revenue ?? null }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={rows} margin={{ top: 6, right: 2, bottom: 0, left: 2 }}>
        <defs>
          <linearGradient id="v17hero" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BLUE} stopOpacity={0.2} />
            <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#8e8e93' }} axisLine={false} tickLine={false} minTickGap={24} />
        <YAxis hide domain={[(m) => m * 0.85, 'dataMax']} />
        <Tooltip cursor={{ stroke: '#d1d1d6' }} contentStyle={tooltipStyle} labelFormatter={shortDate} formatter={(v, k) => [fmt.money(v), k === 'cur' ? 'This period' : 'Previous period']} />
        <Line type="monotone" dataKey="prev" stroke="#c7c7cc" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
        <Area type="monotone" dataKey="cur" stroke={BLUE} strokeWidth={2.25} fill="url(#v17hero)" dot={false} activeDot={{ r: 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function LiveSplit({ live, fmt }) {
  if (!live) return <Skeleton className="h-full w-full" />;
  const total = live.channels.reduce((s, c) => s + c.spend, 0) || 1;
  return (
    <div className="flex h-full flex-col justify-end gap-3">
      <div className="text-xs text-gray-400">Ad spend so far today, by channel</div>
      <div className="flex h-3 overflow-hidden rounded-full bg-white">
        {live.channels.map((c) => <div key={c.name} style={{ width: `${(c.spend / total) * 100}%`, background: CHANNELS[c.name].color }} />)}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
        {live.channels.map((c) => (
          <span key={c.name} className="inline-flex items-center gap-1.5"><img src={CHANNELS[c.name].logo} alt="" className="h-4 w-4 object-contain" />{fmt.compact(c.spend)}</span>
        ))}
      </div>
    </div>
  );
}

function RightNow({ live, liveTot, fmt, className }) {
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead
        title="Right now"
        sub={live ? `Today · updated ${live.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : 'Today'}
        right={(
          <span className="relative mt-1.5 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
        )}
      />
      {!liveTot ? <Skeleton className="mt-4 h-24 w-full" /> : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Mini label="Revenue" value={fmt.compact(liveTot.revenue)} />
            <Mini label="Orders" value={fmt.int(liveTot.orders)} />
            <Mini label="Sessions" value={fmt.kint(liveTot.sessions)} />
          </div>
          <div className="mt-3 rounded-2xl bg-white px-4 py-1">
            {live.channels.map((c) => (
              <div key={c.name} className="flex items-center justify-between border-b border-gray-100 py-2 text-sm last:border-0">
                <span className="inline-flex min-w-0 items-center gap-2"><img src={CHANNELS[c.name].logo} alt="" className="h-4 w-4 object-contain" /><span className="truncate">{c.name}</span></span>
                <span className="tabular-nums text-gray-500">
                  <span className="font-semibold text-gray-900">{fmt.compact(c.spend)}</span>
                  {c.revenue != null && c.spend > 0 && <span className="ml-2">{fmt.x(c.revenue / c.spend)}</span>}
                </span>
              </div>
            ))}
            {!live.channels.length && <div className="py-3 text-sm text-gray-400">No live channel data.</div>}
          </div>
        </>
      )}
    </Tile>
  );
}

function MonthOutlook({ month, fmt, className }) {
  if (!month) return <Tile className={`p-6 ${className}`}><TileHead title="This month" /><Skeleton className="mt-4 h-28 w-full" /></Tile>;
  if (!month.elapsed || !month.cur) return <Tile className={`p-6 ${className}`}><TileHead title="This month" sub="Too early in the month to project." /></Tile>;
  const proj = (month.cur.revenue / month.elapsed) * month.daysInMonth;
  const projSpend = (month.cur.spend / month.elapsed) * month.daysInMonth;
  const vsSame = delta(month.cur.revenue, month.same?.revenue);
  const progress = month.elapsed / month.daysInMonth;
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead title={`${month.name} outlook`} sub={`${month.elapsed} of ${month.daysInMonth} days · run-rate projection`} />
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-[32px] leading-none font-semibold tracking-[-0.03em] tabular-nums">{fmt.compact(proj)}</span>
        <Delta value={delta(proj, month.lastFull?.revenue)} />
      </div>
      <div className="mt-1 text-xs text-gray-400">projected revenue · {month.lastName} closed at {fmt.compact(month.lastFull?.revenue)}</div>
      <div className="mt-4 h-1.5 rounded-full bg-white" title={`${Math.round(progress * 100)}% of the month`}>
        <div className="h-full rounded-full bg-gray-900" style={{ width: `${progress * 100}%` }} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Mini label="Month to date" value={fmt.compact(month.cur.revenue)} />
        <Mini label={`vs ${month.lastName}, same days`} value={vsSame == null ? '—' : `${vsSame >= 0 ? '+' : '−'}${pctText(vsSame)}`} />
        <Mini label="Spend to date" value={fmt.compact(month.cur.spend)} />
        <Mini label="Projected spend" value={fmt.compact(projSpend)} />
      </div>
    </Tile>
  );
}

function Signals({ signals, loading, className }) {
  const dot = { bad: 'bg-rose-500', info: 'bg-amber-400', good: 'bg-emerald-500' };
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead title="Signals" sub="Rule-based checks on spend, conversion and data health" icon={<AlertTriangle className="h-4 w-4 text-gray-400" />} />
      <div className="mt-4 space-y-2">
        {loading ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />) : signals.length === 0 ? (
          <div className="rounded-2xl bg-white px-4 py-6 text-center text-sm text-gray-400">All clear — nothing unusual.</div>
        ) : signals.map((s) => {
          const body = (
            <div className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3 transition-colors hover:bg-white/70">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot[s.tone]}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold leading-snug">{s.title}</div>
                <div className="mt-0.5 text-xs leading-snug text-gray-500">{s.text}</div>
              </div>
              {s.href && <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-300" />}
            </div>
          );
          return s.href ? <Link key={s.title} to={s.href} className="block">{body}</Link> : <div key={s.title}>{body}</div>;
        })}
      </div>
    </Tile>
  );
}

function RoasBadge({ v, fmt }) {
  if (v == null) return <span className="text-gray-300">—</span>;
  const cls = v < 1 ? 'bg-rose-50 text-rose-700' : v < 3 ? 'bg-gray-100 text-gray-700' : 'bg-emerald-50 text-emerald-700';
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{fmt.x(v)}</span>;
}

function Channels({ data, connected, pacing, fmt, sub, className }) {
  const rows = data?.channels;
  const total = rows?.reduce((s, c) => s + c.spend, 0) || 0;
  const expected = pacing?.days_in_month ? pacing.days_passed / pacing.days_in_month : null;
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead title="Channels" sub={sub} right={<Link to="/budget-overview" className="text-[13px] font-medium text-[#0071e3] hover:underline">Budgets</Link>} />
      {rows && total > 0 && (
        <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-white">
          {rows.map((c) => <div key={c.name} style={{ width: `${(c.spend / total) * 100}%`, background: CHANNELS[c.name].color }} title={`${c.name} ${Math.round((c.spend / total) * 100)}%`} />)}
        </div>
      )}
      <div className="mt-4 overflow-x-auto rounded-2xl bg-white">
        <table className="w-full min-w-[620px] text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
              <th className="px-4 pb-2 pt-3 font-medium">Channel</th>
              <th className="px-3 pb-2 pt-3 text-right font-medium">Spend</th>
              <th className="px-3 pb-2 pt-3 text-right font-medium">Share</th>
              <th className="px-3 pb-2 pt-3 text-right font-medium">Revenue</th>
              <th className="px-3 pb-2 pt-3 text-right font-medium">ROAS</th>
              <th className="w-[30%] px-4 pb-2 pt-3 font-medium">This month</th>
            </tr>
          </thead>
          <tbody>
            {!rows ? [0, 1, 2].map((i) => <tr key={i}><td colSpan={6} className="px-4 py-3"><Skeleton className="h-5 w-full" /></td></tr>) : rows.map((c) => {
              const meta = CHANNELS[c.name];
              const pace = pacing?.channels?.find((p) => p.channel_key === meta.pacing);
              const target = pace?.target_spend ? Number(pace.target_spend) : null;
              const pct = target ? Number(pace.mtd_spend || 0) / target : null;
              const ahead = pct != null && expected != null && pct > expected + 0.1;
              const roas = meta.reportsRevenue && c.spend ? (c.revenue || 0) / c.spend : null;
              return (
                <tr key={c.name} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <Link to={meta.href} className="inline-flex items-center gap-2.5 font-medium hover:underline">
                      <img src={meta.logo} alt="" className="h-5 w-5 object-contain" />{c.name}
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <div className="font-semibold">{fmt.compact(c.spend)}</div>
                    <Delta value={delta(c.spend, c.prevSpend)} invert className="!text-[11px]" />
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-500">{total ? `${Math.round((c.spend / total) * 100)}%` : '—'}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{meta.reportsRevenue ? fmt.compact(c.revenue || 0) : <span className="text-gray-300" title="This source doesn't report conversion value">n/a</span>}</td>
                  <td className="px-3 py-3 text-right tabular-nums"><RoasBadge v={roas} fmt={fmt} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-between text-[11px] text-gray-400">
                      <span>{fmt.compact(pace?.mtd_spend)}</span>
                      <span className={ahead ? 'font-medium text-amber-600' : ''}>{target ? `${Math.round(pct * 100)}% of ${fmt.compact(target)}` : 'no target'}</span>
                    </div>
                    <div className="relative mt-1.5 h-1.5 rounded-full bg-gray-100">
                      {target && <div className={`h-full rounded-full ${ahead ? 'bg-amber-500' : 'bg-gray-900'}`} style={{ width: `${Math.min(pct, 1) * 100}%` }} />}
                      {target && expected != null && <div className="absolute -top-1 h-3.5 w-0.5 rounded bg-[#0071e3]" style={{ left: `${expected * 100}%` }} title="Where spend should be today" />}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows && !rows.length && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">{connected?.length ? 'No ad spend in this period.' : 'No ad channels connected.'}</td></tr>}
          </tbody>
        </table>
      </div>
    </Tile>
  );
}

/** GA4 kaynak/ortam adlarını insan diline çevirir (tanınmayan olduğu gibi kalır). */
const SOURCE_NAMES = {
  'google / cpc': 'Google Ads', '(direct) / (none)': 'Direct', 'google / organic': 'Google Search',
  'fbig / dpa': 'Meta catalog ads', 'ig / social': 'Instagram', 'facebook / social': 'Facebook',
  'bing / organic': 'Bing', 'yandex / organic': 'Yandex', 'chatgpt.com / referral': 'ChatGPT',
};

function Sources({ data, fmt, sub, className }) {
  const rows = data?.sources;
  const max = rows?.length ? Math.max(...rows.map((r) => r.revenue)) : 1;
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead title="Where revenue comes from" sub={`GA4 source / medium · ${sub}`} />
      <div className="mt-4 space-y-3">
        {!rows ? [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9 w-full" />) : rows.map((r) => (
          <div key={r.name}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate">
                <span className="font-medium">{SOURCE_NAMES[r.name] || r.name}</span>
                {SOURCE_NAMES[r.name] && <span className="ml-1.5 text-xs text-gray-400">{r.name}</span>}
              </span>
              <span className="shrink-0 tabular-nums">
                <span className="font-semibold">{fmt.compact(r.revenue)}</span>
                <Delta value={delta(r.revenue, r.prev)} className="ml-1.5 !text-[11px]" />
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-white"><div className="h-full rounded-full bg-gray-900" style={{ width: `${(r.revenue / max) * 100}%` }} /></div>
              <span className="w-24 text-right text-[11px] tabular-nums text-gray-400">{fmt.kint(r.sessions)} sessions</span>
            </div>
          </div>
        ))}
      </div>
    </Tile>
  );
}

function Campaigns({ data, fmt, sub, className }) {
  const rows = data?.campaigns?.slice(0, 8);
  const top = data?.campaigns?.[0]?.spend || 0;
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead title="Top campaigns by spend" sub={sub} />
      <div className="mt-4 rounded-2xl bg-white">
        {!rows ? [0, 1, 2, 3].map((i) => <div key={i} className="px-4 py-3"><Skeleton className="h-5 w-full" /></div>) : rows.map((c, i) => {
          const meta = CHANNELS[c.platform];
          const roas = meta?.reportsRevenue && c.spend ? (c.revenue || 0) / c.spend : null;
          return (
            <div key={`${c.platform}-${c.name}`} className={`grid grid-cols-[minmax(0,1fr)_minmax(60px,22%)_64px_64px] items-center gap-3 px-4 py-2.5 text-sm ${i ? 'border-t border-gray-100' : ''}`}>
              <div className="flex min-w-0 items-center gap-2.5">
                {meta && <img src={meta.logo} alt="" className="h-4 w-4 shrink-0 object-contain" />}
                <span className="truncate font-medium" title={c.name}>{c.name}</span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100"><div className="h-full rounded-full bg-gray-900" style={{ width: `${top ? (c.spend / top) * 100 : 0}%` }} /></div>
              <span className="text-right font-semibold tabular-nums">{fmt.compact(c.spend)}</span>
              <span className="text-right"><RoasBadge v={roas} fmt={fmt} /></span>
            </div>
          );
        })}
        {rows && !rows.length && <div className="px-4 py-6 text-center text-sm text-gray-400">No campaigns with spend.</div>}
      </div>
    </Tile>
  );
}

const DEVICE_ICON = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };
const DEVICE_SHADE = ['bg-gray-900', 'bg-gray-500', 'bg-gray-300', 'bg-gray-200'];

function Devices({ data, fmt, sub, className }) {
  const rows = data?.devices?.rows;
  const total = data?.devices?.sessions || 1;
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead title="Devices" sub={`GA4 · ${sub}`} />
      {!rows ? <Skeleton className="mt-4 h-40 w-full" /> : (
        <>
          <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-white">
            {rows.map((r, i) => <div key={r.name} className={DEVICE_SHADE[i] || 'bg-gray-200'} style={{ width: `${(r.sessions / total) * 100}%` }} />)}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {rows.map((r, i) => {
              const Icon = DEVICE_ICON[r.name] || Monitor;
              return (
                <div key={r.name} className="rounded-2xl bg-white px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[13px] font-medium capitalize text-gray-500">
                    <span className={`h-2 w-2 rounded-full ${DEVICE_SHADE[i] || 'bg-gray-200'}`} /><Icon className="h-3.5 w-3.5" />{r.name}
                  </div>
                  <div className="mt-1 text-[20px] font-semibold tabular-nums tracking-[-0.02em]">{fmt.pct(r.sessions / total, 0)}</div>
                  <div className="text-[11px] text-gray-400">of sessions</div>
                  <div className="mt-2 space-y-0.5 text-xs tabular-nums">
                    <div className="flex justify-between"><span className="text-gray-400">Revenue</span><span className="font-medium">{fmt.compact(r.revenue)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">CR</span><span className="font-medium">{fmt.pct(div(r.orders, r.sessions))}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">AOV</span><span className="font-medium">{fmt.compact(div(r.revenue, r.orders))}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
          <DeviceInsight rows={rows} fmt={fmt} />
        </>
      )}
    </Tile>
  );
}

/** Mobil ↔ masaüstü dönüşüm farkı: tek cümle, veriden. */
function DeviceInsight({ rows, fmt }) {
  const m = rows.find((r) => r.name === 'mobile');
  const d = rows.find((r) => r.name === 'desktop');
  const mcr = m && div(m.orders, m.sessions);
  const dcr = d && div(d.orders, d.sessions);
  if (!mcr || !dcr) return null;
  const hi = dcr >= mcr ? ['Desktop', dcr, 'mobile', mcr] : ['Mobile', mcr, 'desktop', dcr];
  const ratio = hi[1] / hi[3];
  if (ratio < 1.15) return null;
  const share = div(m.sessions, rows.reduce((t, r) => t + r.sessions, 0));
  return (
    <p className="mt-4 rounded-2xl bg-white px-4 py-3 text-sm leading-snug text-gray-600">
      <span className="font-semibold text-gray-900">{hi[0]} converts {ratio.toFixed(1)}x better than {hi[2]}</span>
      {' '}({fmt.pct(hi[1])} vs {fmt.pct(hi[3])}){share ? `, while mobile brings ${fmt.pct(share, 0)} of sessions.` : '.'}
    </p>
  );
}

function Weekdays({ rows, fmt, className }) {
  const best = rows?.length ? rows.reduce((a, b) => (b.revenue > a.revenue ? b : a)) : null;
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead title="Best days to sell" sub="Average daily revenue · last 8 weeks" />
      {!rows ? <Skeleton className="mt-4 h-48 w-full" /> : (
        <>
          <div className="mt-2 text-sm text-gray-500">
            {best && <><span className="font-semibold text-gray-900">{WEEKDAY_LONG[best.day]}</span> is strongest at {fmt.compact(best.revenue)} a day.</>}
          </div>
          <div className="mt-3 h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#8e8e93' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={tooltipStyle} formatter={(v) => [fmt.money(v), 'Avg. revenue']} />
                <Bar dataKey="revenue" radius={[8, 8, 8, 8]}>
                  {rows.map((r) => <Cell key={r.day} fill={r.day === best?.day ? BLUE : '#d1d1d6'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Tile>
  );
}

function SearchTile({ gsc, fmt, className }) {
  if (gsc?.none) return null;
  const c = gsc?.cur;
  const p = gsc?.prev;
  return (
    <Tile className={`p-6 ${className}`}>
      <TileHead
        title="Organic search"
        icon={<Search className="h-4 w-4 text-gray-400" />}
        sub={gsc?.last ? `Search Console · ${shortDate(gsc.start)} – ${shortDate(gsc.last)} (latest available) vs previous 7 days` : 'Search Console'}
        right={<Link to="/search-console/overview" className="text-[13px] font-medium text-[#0071e3] hover:underline">Open</Link>}
      />
      {!gsc ? <Skeleton className="mt-4 h-24 w-full" /> : !gsc.last ? (
        <div className="mt-4 text-sm text-gray-400">No Search Console data yet.</div>
      ) : (
        <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="grid grid-cols-2 content-start gap-2 sm:grid-cols-4">
            <GscStat label="Clicks" value={fmt.int(c?.['s:clicks'])} d={delta(c?.['s:clicks'], p?.['s:clicks'])} />
            <GscStat label="Impressions" value={fmt.kint(c?.['s:impressions'])} d={delta(c?.['s:impressions'], p?.['s:impressions'])} />
            <GscStat label="CTR" value={fmt.pct(c?.['s:ctr'])} d={delta(c?.['s:ctr'], p?.['s:ctr'])} />
            <GscStat label="Avg. position" value={c?.['s:avg_position'] != null ? c['s:avg_position'].toFixed(1) : '—'} d={delta(c?.['s:avg_position'], p?.['s:avg_position'])} invert />
          </div>
          <div className="rounded-2xl bg-white px-4 py-1">
            {gsc.top.map((r, i) => (
              <div key={r.dims[0]} className={`flex items-center justify-between gap-3 py-2 text-sm ${i ? 'border-t border-gray-100' : ''}`}>
                <span className="min-w-0 truncate"><span className="mr-2 text-xs tabular-nums text-gray-300">{i + 1}</span>{r.dims[0]}</span>
                <span className="shrink-0 tabular-nums text-gray-500"><span className="font-semibold text-gray-900">{fmt.int(r['s:clicks'])}</span> clicks · #{r['s:avg_position']?.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Tile>
  );
}

const GscStat = ({ label, value, d, invert }) => (
  <div className="rounded-2xl bg-white px-4 py-3">
    <div className="text-[11px] text-gray-400">{label}</div>
    <div className="mt-0.5 flex items-baseline gap-1.5"><span className="text-[19px] font-semibold tabular-nums">{value}</span><Delta value={d} invert={invert} className="!text-[11px]" /></div>
  </div>
);

function TrendChart({ data, metric, fmt }) {
  const rows = data.map((d) => ({ date: d.date, cur: d.cur?.[metric.key] ?? null, prev: d.prev?.[metric.key] ?? null }));
  const f = metric.kind === 'money' ? fmt.compact : metric.kind === 'x' ? fmt.x : fmt.kint;
  if (!rows.some((r) => r.cur != null)) return <div className="flex h-full items-center justify-center text-sm text-gray-400">No data for this range.</div>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={rows} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id="v17trend" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BLUE} stopOpacity={0.16} />
            <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="#e5e5ea" />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#8e8e93' }} axisLine={false} tickLine={false} minTickGap={28} />
        <YAxis tickFormatter={f} tick={{ fontSize: 11, fill: '#8e8e93' }} axisLine={false} tickLine={false} width={60} />
        <Tooltip cursor={{ stroke: '#d1d1d6' }} contentStyle={tooltipStyle} labelFormatter={shortDate} formatter={(v, k) => [f(v), k === 'cur' ? 'This period' : 'Previous period']} />
        <Line type="monotone" dataKey="prev" stroke="#c7c7cc" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
        <Area type="monotone" dataKey="cur" stroke={BLUE} strokeWidth={2.25} fill="url(#v17trend)" dot={false} activeDot={{ r: 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
